import React from "react";

export const MANUAL_MIN_URLS = 1;
export const MANUAL_MAX_URLS = 25;

function urlKey(url) {
  return url
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+(?=\?|$)/, "");
}

/** One URL per line (spaces/commas also split); trimmed, de-duplicated (www./trailing slash ignored). */
export function parseCategoryUrls(text) {
  const seen = new Set();
  const out = [];
  for (const raw of String(text || "").split(/[\s,]+/)) {
    const url = raw.trim();
    if (!url) continue;
    const key = urlKey(url);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(url);
  }
  return out;
}

function hostOf(url) {
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** Client-side hint only — the server validates every URL. */
export function foreignUrls(urls, domain) {
  const bare = String(domain || "").toLowerCase().replace(/^www\./, "");
  if (!bare) return [];
  return urls.filter((u) => hostOf(u) !== bare);
}

export function manualUrlsReady(urls, domain) {
  return urls.length >= MANUAL_MIN_URLS && urls.length <= MANUAL_MAX_URLS && foreignUrls(urls, domain).length === 0;
}

const STATUS_LABEL = {
  verified: "Products found",
  unverified: "Not checked yet",
  no_products: "No products found",
  access_failed: "Couldn't open",
};

export default function ManualCategoryUrls({
  id,
  value,
  onChange,
  domain,
  saved = [],
  disabled = false,
  optional = false,
}) {
  const urls = parseCategoryUrls(value);
  const foreign = foreignUrls(urls, domain);
  const tooMany = urls.length > MANUAL_MAX_URLS;
  const example = domain ? `https://www.${String(domain).replace(/^www\./, "")}/category/tools` : "https://example.com/category/tools";
  const addSaved = (url) => {
    if (urls.some((u) => urlKey(u) === urlKey(url))) return;
    onChange([...urls, url].join("\n"));
  };
  let message = optional
    ? `Optional — paste up to ${MANUAL_MAX_URLS} category URLs, one per line. Keyword search works without them.`
    : `Enter ${MANUAL_MIN_URLS} to ${MANUAL_MAX_URLS} category URLs, one per line.`;
  if (tooMany) message = `Use at most ${MANUAL_MAX_URLS} category URLs (${urls.length} entered).`;
  else if (foreign.length) message = `Every URL must be on ${domain}.`;
  else if (urls.length) message = `${urls.length} of ${MANUAL_MAX_URLS} category URLs.`;
  const invalid = tooMany || foreign.length > 0;

  return (
    <div className="as-manual-urls" data-testid="manual-category-urls">
      <textarea
        id={id}
        className="as-textarea"
        data-testid="manual-category-urls-input"
        value={value}
        disabled={disabled}
        rows={5}
        aria-invalid={invalid || undefined}
        aria-describedby={id ? `${id}-hint` : undefined}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter category URLs, one per line\n\n${example}`}
      />
      <div
        id={id ? `${id}-hint` : undefined}
        className={invalid ? "as-helper as-helper-error" : "as-helper"}
        data-testid="manual-category-urls-hint"
        aria-live="polite"
      >
        {message}
      </div>
      {saved.length > 0 && (
        <ul className="as-manual-saved" aria-label="Saved category URLs" data-testid="manual-category-saved">
          {saved.map((s) => (
            <li key={s.url} className="as-manual-saved-row">
              <span className="as-manual-saved-url" title={s.url}>{s.url}</span>
              <span className={`as-manual-saved-status is-${s.status}`}>{STATUS_LABEL[s.status] || s.status}</span>
              <button type="button" className="as-retry" disabled={disabled} onClick={() => addSaved(s.url)}>
                Add
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
